import React, { useEffect, useMemo, useState } from 'react';
import { PlusIcon, SearchIcon } from 'lucide-react';
import { useBilling } from '../../contexts/BillingContext';
import { BillingNavigator, DirectSale } from '../../types/billing';
import { formatBillingDate, formatMoney, fromN, toN, todayLusaka } from '../../utils/billingMoney';
import { formatReceiptTime } from '../../utils/billingTime';
import { exportCsv } from '../../utils/hrExport';
import { Button } from '../../components/ui/Button';
import { DocStatusBadge } from './DocStatusBadge';
import { BillingPrintModal } from './BillingPrintModal';
import { DirectSaleReceiptPaper } from './DirectSaleReceiptPaper';
import { ReasonDialog } from './ReasonDialog';

/** Register of cash sales / direct receipts. Reprinting never records a new sale. */
export function DirectSalesList({
  nav,
  openId,
  onOpened
}: {
  nav: BillingNavigator;
  openId?: string | null;
  onOpened?: () => void;
}) {
  const {
    sales,
    config,
    can,
    voidDirectSale
  } = useBilling();
  const [query, setQuery] = useState('');
  const [from, setFrom] = useState(todayLusaka().slice(0, 8) + '01');
  const [to, setTo] = useState(todayLusaka());
  const [status, setStatus] = useState<'all' | 'Completed' | 'Void'>('all');
  const [printing, setPrinting] = useState<{
    sale: DirectSale;
    action: 'view' | 'print' | 'download';
  } | null>(null);
  const [voiding, setVoiding] = useState<DirectSale | null>(null);
  const [message, setMessage] = useState('');
  const fmt = (v: number) => formatMoney(v, config.regional.currencySymbol);
  const opened = openId ? sales.find(s => s.id === openId) : undefined;
  useEffect(() => {
    if (!opened) return;
    setPrinting({
      sale: opened,
      action: 'view'
    });
    onOpened?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened?.id]);
  const rows = useMemo(() => {
    const q = query.trim().toLowerCase();
    return sales.filter(s => (status === 'all' || s.state === status) && (!from || s.date >= from) && (!to || s.date <= to)).filter(s => !q || [s.number, s.accountName, s.bmcFileNumber, s.phone, s.cashierName, ...s.lines.map(l => l.description)].some(v => v?.toLowerCase().includes(q))).sort((a, b) => b.createdAt.localeCompare(a.createdAt));
  }, [sales, query, status, from, to]);
  const completed = rows.filter(s => s.state === 'Completed');
  const total = fromN(completed.reduce((s, r) => s + toN(r.total), 0));
  return <div className="space-y-3" data-id="element-3436" data-mp="2s0gu6lrabwx1">
      <div className="flex flex-wrap items-center justify-between gap-3" data-id="element-3437" data-mp="2s1a05kif6ie4">
        <div data-id="element-3438" data-mp="2wys40whky7f9">
          <h1 className="text-xl font-bold text-slate-900" data-id="element-3439" data-mp="2wzblkc3bitsr">Cash Sales / Direct Receipts</h1>
          <p className="text-sm text-slate-500" data-id="element-3440" data-mp="2j7n9g8t2k5bs">Sales paid in full at the counter, without an invoice.</p>
        </div>
        <div className="flex gap-2" data-id="element-3441" data-mp="2n8785gtajh01">
          {can('exportData') && <Button size="sm" variant="outline" onClick={() => exportCsv(`Direct-sales-${from}-to-${to}`, [{
          key: 'number',
          label: 'Receipt'
        }, {
          key: 'date',
          label: 'Date'
        }, {
          key: 'customer',
          label: 'Customer'
        }, {
          key: 'cashier',
          label: 'Cashier'
        }, {
          key: 'methods',
          label: 'Paid by'
        }, {
          key: 'status',
          label: 'Status'
        }, {
          key: 'net',
          label: 'Net excl. tax',
          align: 'right'
        }, {
          key: 'tax',
          label: 'Tax',
          align: 'right'
        }, {
          key: 'total',
          label: 'Total',
          align: 'right'
        }], rows.map(s => ({
          number: s.number,
          date: s.date,
          customer: s.accountName,
          cashier: s.cashierName,
          methods: s.tenders.map(t => `${t.methodName} ${t.amount.toFixed(2)}`).join(' + '),
          status: s.state,
          net: fromN(toN(s.total) - toN(s.taxTotal)),
          tax: s.taxTotal,
          total: s.total
        })))} data-id="element-3442" data-mp="3azckhm00vr5l">Export CSV</Button>}
          {can('directSales') && config.sales.directSalesEnabled && <Button size="sm" onClick={() => nav.newDirectSale()} data-id="element-3443" data-mp="qbugjauljhaa"><PlusIcon className="mr-1 h-4 w-4" aria-hidden="true" data-id="element-3444" data-mp="q9zwdyr5ft79" />New cash sale</Button>}
        </div>
      </div>

      <div className="flex flex-wrap items-end gap-2 rounded-lg border border-slate-200 bg-white p-3" data-id="element-3445" data-mp="26rxe9s0u64ct">
        <div className="relative min-w-[220px] flex-1" data-id="element-3446" data-mp="21su6hzth1k15">
          <label htmlFor="ds-search" className="sr-only" data-id="element-3447" data-mp="banv1vznk35n">Search sales</label>
          <SearchIcon className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" aria-hidden="true" data-id="element-3448" data-mp="g8784skm69w0" />
          <input id="ds-search" value={query} onChange={e => setQuery(e.target.value)} placeholder="Receipt, customer, item or cashier" className="w-full rounded-md border border-slate-300 py-2 pl-8 pr-3 text-sm" data-id="element-3449" data-mp="k99dzs91dk6y" />
        </div>
        <div data-id="element-3450" data-mp="u88cii037zg8"><label htmlFor="ds-status" className="block text-[11px] font-medium text-slate-500" data-id="element-3451" data-mp="u6dy7mjbn5l4">Status</label><select id="ds-status" value={status} onChange={e => setStatus(e.target.value as typeof status)} className="mt-0.5 rounded-md border border-slate-300 bg-white px-2 py-2 text-sm" data-id="element-3452" data-mp="z5guoil8k3hh"><option value="all" data-id="element-3453" data-mp="17zdevruub5u9">All</option><option value="Completed" data-id="element-3454" data-mp="181prrqlqqq0g">Completed</option><option value="Void" data-id="element-3455" data-mp="1czreqmmaf9h7">Void</option></select></div>
        <div data-id="element-3456" data-mp="1cx55mijr0lwv"><label htmlFor="ds-from" className="block text-[11px] font-medium text-slate-500" data-id="element-3457" data-mp="1hy2l2nddj1uo">From</label><input id="ds-from" type="date" value={from} onChange={e => setFrom(e.target.value)} className="mt-0.5 rounded-md border border-slate-300 px-2 py-1.5 text-sm" data-id="element-3458" data-mp="1hvq52fr6f1k9" /></div>
        <div data-id="element-3459" data-mp="1n1lid31xlmwg"><label htmlFor="ds-to" className="block text-[11px] font-medium text-slate-500" data-id="element-3460" data-mp="1n1lmnf8ys929">To</label><input id="ds-to" type="date" value={to} onChange={e => setTo(e.target.value)} className="mt-0.5 rounded-md border border-slate-300 px-2 py-1.5 text-sm" data-id="element-3461" data-mp="1i4buvsy3rlb9" /></div>
      </div>

      {message && <p role="status" className="rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-800" data-id="element-3462" data-mp="2arzfv93acdyf">{message}</p>}

      <div className="overflow-hidden rounded-lg border border-slate-200 bg-white" data-id="element-3463" data-mp="212o7xps4xfc4">
        {rows.length === 0 ? <p className="py-14 text-center text-sm text-slate-500" data-id="element-3464" data-mp="1x12qrs8kiew4">{sales.length ? 'No sales match these filters.' : 'No direct sales yet.'}</p> : <div className="overflow-x-auto" data-id="element-3465" data-mp="2tjs6wxysnp3i">
            <table className="min-w-full text-sm" data-id="element-3466" data-mp="2osa5k8suzmi0">
              <thead className="bg-slate-50 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-500" data-id="element-3467" data-mp="2orgwv2acamx8">
                <tr data-id="element-3468" data-mp="mvi0zcdsohat"><th className="px-3 py-2" data-id="element-3469" data-mp="mwt6phqsvz4k">Receipt</th><th className="px-3 py-2" data-id="element-3470" data-mp="ruckkgjl69vz">Date · time</th><th className="px-3 py-2" data-id="element-3471" data-mp="rt9vme5tqhip">Customer</th><th className="px-3 py-2" data-id="element-3472" data-mp="d4wwj8bfwijz">Items</th><th className="px-3 py-2" data-id="element-3473" data-mp="d5orqi14ke9q">Paid by</th><th className="px-3 py-2" data-id="element-3474" data-mp="d4n5riikm7d8">Cashier</th><th className="px-3 py-2 text-right" data-id="element-3475" data-mp="i3qat2yjwdl7">Total</th><th className="px-3 py-2" data-id="element-3476" data-mp="i2f7t5cxj9v5">Status</th><th className="px-3 py-2" data-id="element-3477" data-mp="i1nc7ujc1c4v"><span className="sr-only" data-id="element-3478" data-mp="43pbvt1ujg8d">Actions</span></th></tr>
              </thead>
              <tbody className="divide-y divide-slate-100" data-id="element-3479" data-mp="931ws895qd8b">
                {rows.map(s => <tr key={s.id} className="hover:bg-slate-50" data-id="element-3480" data-mp="3qbovasabl26x">
                    <td className="whitespace-nowrap px-3 py-2" data-id="element-3481" data-mp="3qd9pggsq2j49"><button type="button" className="font-semibold text-indigo-700 hover:underline" onClick={() => setPrinting({
                  sale: s,
                  action: 'view'
                })} data-id="element-3482" data-mp="3va05530m7cp5">{s.number}</button></td>
                    <td className="whitespace-nowrap px-3 py-2" data-id="element-3483" data-mp="1tj6k407uy39r">{formatBillingDate(s.date, config.regional.dateFormat)} <span className="text-xs text-slate-500" data-id="element-3484" data-mp="3e17ci30hhi4p">{formatReceiptTime(s.createdAt, config.regional.timeFormat)}</span></td>
                    <td className="px-3 py-2" data-id="element-3485" data-mp="392w47wpu7fol">{s.accountName}{s.bmcFileNumber && <span className="block text-xs text-slate-500" data-id="element-3486" data-mp="3909vhtr7mu4s">{s.bmcFileNumber}</span>}</td>
                    <td className="max-w-[240px] truncate px-3 py-2 text-xs text-slate-600" data-id="element-3487" data-mp="3nstxgr2mivkn">{s.lines.map(l => `${l.quantity}× ${l.description}`).join(', ')}</td>
                    <td className="px-3 py-2 text-xs" data-id="element-3488" data-mp="l00t6pngr71">{s.tenders.map(t => t.methodName).join(' + ')}</td>
                    <td className="px-3 py-2 text-xs" data-id="element-3489" data-mp="3spk8gzzkn0z7">{s.cashierName}</td>
                    <td className={`whitespace-nowrap px-3 py-2 text-right font-semibold tabular-nums ${s.state === 'Void' ? 'text-slate-400 line-through' : ''}`} data-id="element-3490" data-mp="ajfot0tdrkcb">{fmt(s.total)}</td>
                    <td className="px-3 py-2" data-id="element-3491" data-mp="2cbc0qbjgmw7m"><DocStatusBadge status={s.state} data-id="element-3492" data-mp="2ccn3c8165xx5" /></td>
                    <td className="whitespace-nowrap px-3 py-2 text-right" data-id="element-3493" data-mp="27d0oxv9lubmr">
                      <div className="inline-flex gap-1" data-id="element-3494" data-mp="27f3pvacapp69">
                        <Button size="sm" variant="ghost" onClick={() => setPrinting({
                    sale: s,
                    action: 'print'
                  })} data-id="element-3495" data-mp="1litqd7eqth1d">Reprint</Button>
                        {s.state === 'Completed' && can('voids') && <Button size="sm" variant="ghost" onClick={() => setVoiding(s)} data-id="element-3496" data-mp="1v8v5h9p4bp45">Void</Button>}
                      </div>
                    </td>
                  </tr>)}
              </tbody>
              <tfoot className="border-t-2 border-slate-200 bg-slate-50 font-semibold" data-id="element-3497" data-mp="12lsk7j9gdavu"><tr data-id="element-3498" data-mp="17jk904eswgvt"><td className="px-3 py-2" colSpan={6} data-id="element-3499" data-mp="17jkdohpqx525">{completed.length} completed sale{completed.length === 1 ? '' : 's'} (voids excluded)</td><td className="px-3 py-2 text-right tabular-nums" data-id="element-3500" data-mp="1chvi2cx9uncz">{fmt(total)}</td><td colSpan={2} data-id="element-3501" data-mp="1hhqcgqg99v2s" /></tr></tfoot>
            </table>
          </div>}
      </div>

      {printing && <BillingPrintModal title={`Receipt ${printing.sale.number}`} filename={`Receipt-${printing.sale.number}`} paper="A4" initialAction={printing.action} onClose={() => setPrinting(null)} data-id="element-3502" data-mp="2nyvdviitvkbr">
          <DirectSaleReceiptPaper sale={printing.sale} data-id="element-3503" data-mp="ha3166djifww" />
        </BillingPrintModal>}
      {voiding && <ReasonDialog title={`Void ${voiding.number}`} description="The sale stays on record marked VOID, is removed from sales reports, and any stock it used is returned. Refund the customer separately." confirmLabel="Void sale" onConfirm={r => voidDirectSale(voiding.id, r)} onClose={() => setVoiding(null)} onDone={m => {
      setVoiding(null);
      setMessage(m ?? 'Voided.');
    }} data-id="element-3504" data-mp="cagpi1dd17q7" />}
    </div>;
}